import React, { useState, useEffect, useCallback, useRef } from "react";
import NavBar from "../../../common/navbar";
import { baseUrl, getRequestOptions, postRequestOptions, handleAuth } from "../../../common/cookie";
import Loader from "../../../common/loader";
import axios from "axios";
import { formatDate, CloseIcon, CopyableCell } from "../../../common/functions";
import { tableTitlesBackOffice, longTitlesBO, smallTitlesBO, mediumTitlesBO } from "../tools/titlesPortfolio";
import { widthScreen, widthMobile } from "./viewPortfolio";
import ViewTradeHistory from "../tools/viewTradeHistory";

function HiddenPositions() {
  let [hiddenPositions, setHiddenPositions] = useState<any>(null);
  let [request, setRequestStatus] = useState(false);
  let [restoreConfirm, setRestoreConfirm] = useState<any>(null);
  let [restoreDisplay, setRestoreDisplay] = useState("none");
  let [vconDisplay, setVconDisplay] = useState("none");
  let [vconStep, setVconStep] = useState<number>(1);
  let [parsedVcon, setParsedVcon] = useState<any>(null);
  let [vconPositionLocation, setVconPositionLocation] = useState("");
  let [message, setMessage] = useState<any>(null);
  let [selectedItem, setSelectedItem] = useState<any>(null);
  let [positionInfo, setPositionInfo] = useState<any>(null);
  let [tradeHistoryCardDisplay, setTradeHistoryCardDisplay] = useState("none");
  let [screenWidth, setScreenWidth] = useState(window.innerWidth);

  let [contextMenuState, setContextMenuState] = useState({ visible: false, x: 0, y: 0 });
  let contextMenuRef: any = useRef(null);

  useEffect(() => {
    let handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  let handleContextMenu = useCallback((event: any, item: any) => {
    event.preventDefault();
    let scrollX = window.scrollX || window.pageXOffset;
    let scrollY = window.scrollY || window.pageYOffset;
    let x = event.clientX + scrollX;
    let y = event.clientY + scrollY;
    setSelectedItem(item);
    setPositionInfo(item.position || {});
    setContextMenuState({ visible: true, x: x, y: y });
  }, []);

  let handleClick = useCallback((event: any) => {
    if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
      setContextMenuState({ visible: false, x: 0, y: 0 });
    }
  }, []);

  useEffect(() => {
    fetch(baseUrl + "hidden-positions", getRequestOptions)
      .then((res) => {
        handleAuth(res.status);
        return res.json();
      })
      .then((data) => {
        if (data.error) {
          setHiddenPositions([]);
        } else {
          let positions = data.data || [];
          setHiddenPositions(positions);
        }
      })
      .catch(() => {
        setHiddenPositions([]);
      });
  }, []);

  function showRestoreConfirm() {
    if (!selectedItem) return;
    setRestoreConfirm({ logId: selectedItem.logId, ticker: selectedItem.position ? selectedItem.position["BB Ticker"] : "" });
    setRestoreDisplay("");
    setContextMenuState({ visible: false, x: 0, y: 0 });
  }

  function cancelRestore() {
    setRestoreConfirm(null);
    setRestoreDisplay("none");
  }

  async function handleRestore() {
    if (!restoreConfirm) return;
    let ticker = restoreConfirm.ticker;
    setRequestStatus(true);
    cancelRestore();
    try {
      let payload = { logId: restoreConfirm.logId };
      let action: any = await axios.post(baseUrl + "restore-hidden-position", payload, postRequestOptions);
      if (action.data && action.data.error) {
        let errorText = action.data.error;
        if (action.data.existingPosition) {
          let ep = action.data.existingPosition;
          errorText += " -- " + ep.ticker + " Notional: " + (ep.notional || 0).toLocaleString() + ", Mid: " + (ep.mid || 0).toFixed(4) + ", Avg Cost: " + (ep.avgCost || 0).toFixed(4);
        }
        setMessage({ type: "error", text: errorText });
        setRequestStatus(false);
      } else {
        let msg = "Position restored: " + ticker + ". Update prices after restoring.";
        let validation = action.data && action.data.data ? action.data.data.validation : null;
        if (validation && !validation.match) {
          msg += " | Trade mismatch: Portfolio notional " + validation.positionNotional.toLocaleString() + " vs trade history " + validation.expectedNotional.toLocaleString() + " (gap: " + validation.gap.toLocaleString() + "). Consider recalculating.";
        }
        setMessage({ type: "success", text: msg });
        setRequestStatus(false);
        setTimeout(() => window.location.reload(), 4000);
      }
    } catch (error) {
      setMessage({ type: "error", text: "Error restoring position." });
      setRequestStatus(false);
    }
  }

  function handleViewTradeHistory() {
    if (!selectedItem) return;
    setPositionInfo(selectedItem.position || {});
    setTradeHistoryCardDisplay("block");
    setContextMenuState({ visible: false, x: 0, y: 0 });
  }

  function openVconUpload() {
    if (!selectedItem) return;
    let position = selectedItem.position || {};
    setVconPositionLocation(position["Location"] || "");
    setVconStep(1);
    setParsedVcon(null);
    setVconDisplay("");
    setContextMenuState({ visible: false, x: 0, y: 0 });
  }

  function cancelVcon() {
    setParsedVcon(null);
    setVconStep(1);
    setVconDisplay("none");
  }

  async function handleVconEmlParse(event: any) {
    event.preventDefault();
    setRequestStatus(true);
    setMessage(null);
    try {
      let fileInput = event.target.querySelector('input[type="file"]');
      if (!fileInput || !fileInput.files || !fileInput.files[0]) {
        setMessage({ type: "error", text: "Please select an .eml file" });
        setRequestStatus(false);
        return;
      }
      let formData = new FormData();
      formData.append("emlFile", fileInput.files[0]);
      let auth: any = await axios.post(baseUrl + "upload-vcon-eml", formData, postRequestOptions);
      if (auth.data && auth.data.error) {
        setMessage({ type: "error", text: auth.data.error });
        setRequestStatus(false);
      } else {
        let trade = auth.data.data;
        if (vconPositionLocation && !trade["Location"]) {
          trade["Location"] = vconPositionLocation;
        }
        setParsedVcon(trade);
        setVconStep(2);
        setRequestStatus(false);
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to parse .eml file" });
      setRequestStatus(false);
    }
  }

  async function handleVconConfirmUpload(event: any) {
    event.preventDefault();
    if (!parsedVcon) return;
    let ticker = parsedVcon["BB Ticker"];
    setRequestStatus(true);
    setMessage(null);
    try {
      let action: any = await axios.post(baseUrl + "upload-missing-vcon", parsedVcon, postRequestOptions);
      if (action.data && action.data.error) {
        setMessage({ type: "error", text: action.data.error });
        setRequestStatus(false);
      } else {
        cancelVcon();
        let recalcText = action.data.data && action.data.data.recalculated ? " Position recalculated." : "";
        setMessage({ type: "success", text: "Missing VCON uploaded: " + ticker + "." + recalcText });
        setTimeout(() => window.location.reload(), 3000);
      }
    } catch (error) {
      setMessage({ type: "error", text: "Error uploading VCON." });
      setRequestStatus(false);
    }
  }

  function getPositionGroup(position: any) {
    let strategy = position["Strategy"] || "";
    let type = position["Type"] || "";
    let assetClass = position["Asset Class"] || "";
    if (strategy == "RV") return { order: 1, color: "#FEEBED", group: "RV" };
    if (assetClass == "IG" && type == "BND") return { order: 2, color: "#E1BEE7", group: "IG" };
    if (assetClass == "HY" || assetClass == "Corporate Bond") return { order: 3, color: "#C5CAE9", group: "HY" };
    if (assetClass == "Non-USD" || assetClass == "Non-USD Bonds") return { order: 4, color: "#C5D8DC", group: "Non-USD" };
    if (type == "CDS" || assetClass == "CDS") return { order: 5, color: "#C5DFC5", group: "CDS" };
    if (type == "UST") return { order: 6, color: "#C5E1B5", group: "Treasury" };
    if (type == "FUT" || assetClass == "FUT" || assetClass == "Futures") return { order: 7, color: "#C5DCD0", group: "Futures" };
    if (type == "FX" || assetClass == "FX") return { order: 8, color: "#C5D3E5", group: "FX" };
    if (type == "EQ" || assetClass == "Equity") return { order: 9, color: "#FFF9C4", group: "Equity" };
    if (assetClass == "Illiquid") return { order: 10, color: "#9FA8DA", group: "Illiquid" };
    let notional = parseFloat(position["Notional Amount"]) || 0;
    if (notional == 0) return { order: 11, color: "#4CAF50", group: "Realized" };
    return { order: 12, color: "#E5D1B4", group: "Other" };
  }

  let sortedPositions = hiddenPositions ? [...hiddenPositions].sort((a: any, b: any) => {
    let groupA = getPositionGroup(a.position || {});
    let groupB = getPositionGroup(b.position || {});
    return groupA.order - groupB.order;
  }) : [];

  if (hiddenPositions == null || request) {
    return (
      <div>
        <NavBar />
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <NavBar />
      <div className="portfolio-range-updates-container">
        <h4 className="lastUpdateText">Stale Positions</h4>
        <div className="portfolio-range-picker-container input-search-calendar-container">
          <button className="btn upload-btn no-flex" onClick={async () => {
            setRequestStatus(true);
            try {
              let action: any = await axios.post(baseUrl + "refresh-hidden-positions", {}, postRequestOptions);
              handleAuth(action.status);
              if (action.data && action.data.data) {
                window.location.reload();
              }
            } catch (error) {}
            setRequestStatus(false);
          }}>
            Refresh Data
          </button>
          <button className="btn upload-btn no-flex" onClick={() => (window.location.href = "/view-portfolio")}>
            Back to Portfolio
          </button>
        </div>
      </div>

      {message && (
        <h4 className={message.type == "error" ? "error-text" : "lastUpdateText"}>
          {message.text}
        </h4>
      )}

      {hiddenPositions.length == 0 ? (
        <div className="title">
          <h5>No stale positions</h5>
        </div>
      ) : (
        <div className="table-container-custom" style={{ overflowX: "auto" }}>
          <table id="table-id" onClick={handleClick} className="table table-hover table-portfolio">
            <tbody>
              <tr className="sticky-top">
                {tableTitlesBackOffice.map((title: any, titleIndex: any) => (
                  <td
                    key={titleIndex}
                    className={
                      "copied-text " +
                      (titleIndex < 10 ? " higher-sticky-row " : " lower-sticky-row ") +
                      (titleIndex < 10 ? " higher-sticky-portfolio-row " : "") +
                      (longTitlesBO.includes(title) ? " wider-table-cell" : "") +
                      (smallTitlesBO.includes(title) ? " small-table-cell" : "") +
                      (mediumTitlesBO.includes(title) ? " medium-table-cell" : "")
                    }
                    style={{ left: screenWidth < 1000 ? widthMobile[titleIndex] : widthScreen[titleIndex] }}
                  >
                    {title}
                  </td>
                ))}
                <td className="copied-text lower-sticky-row">Hidden Date</td>
              </tr>
              {sortedPositions.map((item: any, index: number) => {
                let position = item.position || {};
                let group = getPositionGroup(position);
                let rowColor = group.color;
                return (
                  <tr
                    key={index}
                    onContextMenu={(event) => handleContextMenu(event, item)}
                    className="table-body"
                    style={{
                      backgroundColor: rowColor,
                      cursor: "pointer",
                    }}
                  >
                    {tableTitlesBackOffice.map((title: string, ti: number) => {
                      let cellValue = title == "Amortized Notional Value"
                        ? position["Type"] == "BND" ? position["Principal"] : "x"
                        : position[title];
                      if (cellValue && typeof cellValue == "object") cellValue = "";
                      return (
                        <CopyableCell
                          key={ti}
                          text={isFinite(cellValue) && cellValue ? cellValue.toLocaleString() : cellValue}
                          index={ti}
                          columns={10}
                          type={"portfolio"}
                          color={rowColor}
                          left={screenWidth < 1000 ? widthMobile[ti] : widthScreen[ti]}
                        />
                      );
                    })}
                    <CopyableCell key={"hd-" + index} text={item.hiddenDate ? formatDate(item.hiddenDate) : ""} index={0} columns={0} type={"portfolio"} color={""} left={"0px"} />
                  </tr>
                );
              })}

              {contextMenuState.visible && (
                <div
                  ref={contextMenuRef}
                  style={{ position: "absolute", top: contextMenuState.y, left: contextMenuState.x }}
                  className="context-menu-container"
                >
                  <div className="context-menu">
                    <div className="context-menue-row">
                      <p className="context-menu-text" onClick={() => showRestoreConfirm()}>
                        Restore Position
                      </p>
                    </div>
                    <div className="context-menue-row">
                      <p className="context-menu-text" onClick={() => openVconUpload()}>
                        Upload Missing VCON (.eml)
                      </p>
                    </div>
                  </div>
                  <hr className="hr" />
                  <div className="context-menu">
                    <div className="context-menue-row">
                      <p className="context-menu-text" onClick={() => handleViewTradeHistory()}>
                        View Full Trade History
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="edit-info-container-1" style={{ display: restoreDisplay }} onDoubleClick={() => cancelRestore()}>
        {restoreConfirm && (
          <div className="edit-position-modal" style={{ maxWidth: "500px" }}>
            <div className="edit-position-header">
              <h3 className="edit-position-title">Restore Position: {restoreConfirm.ticker}</h3>
              <div onClick={() => cancelRestore()} className="close-icon-container-edit-position">
                <CloseIcon />
              </div>
            </div>
            <div className="edit-position-grid" style={{ gridTemplateColumns: "1fr" }}>
              <div className="edit-section">
                <div className="edit-section-title">Confirmation</div>
                <div className="edit-section-fields">
                  <div className="edit-field-row">
                    <label className="edit-field-label">Action</label>
                    <input type="text" className="edit-position-input" value="Position will reappear in portfolio with original data" disabled />
                  </div>
                  <div className="edit-field-row">
                    <label className="edit-field-label">Note</label>
                    <input type="text" className="edit-position-input" value="Update prices after restoring" disabled />
                  </div>
                </div>
              </div>
            </div>
            <div className="edit-position-footer">
              <button type="button" className="btn btn-cancel" onClick={() => cancelRestore()}>Cancel</button>
              <button type="button" className="btn upload-btn" onClick={() => handleRestore()}>Confirm Restore</button>
            </div>
          </div>
        )}
      </div>

      <div className="edit-info-container-1" style={{ display: vconDisplay }} onDoubleClick={() => cancelVcon()}>
        <div className="edit-position-modal">
          <div className="edit-position-header">
            <h3 className="edit-position-title">Upload Missing VCON (.eml)</h3>
            <div onClick={() => cancelVcon()} className="close-icon-container-edit-position">
              <CloseIcon />
            </div>
          </div>
          {vconStep === 1 && (
            <form onSubmit={handleVconEmlParse}>
              <div className="edit-section" style={{ padding: "15px" }}>
                <div className="edit-section-title">Select Bloomberg VCON Email</div>
                <div className="edit-section-fields" style={{ padding: "10px 0" }}>
                  <input type="file" accept=".eml" required style={{ fontSize: "12px" }} />
                  <p style={{ fontSize: "10px", color: "#888", marginTop: "6px" }}>Select a .eml file from a Bloomberg VCON trade notification</p>
                </div>
              </div>
              <div className="edit-position-footer">
                <button type="button" className="btn btn-cancel" onClick={() => cancelVcon()}>Cancel</button>
                <button type="submit" className="btn upload-btn" disabled={request}>{request ? "Parsing..." : "Parse VCON"}</button>
              </div>
            </form>
          )}
          {vconStep === 2 && parsedVcon && (
            <form onSubmit={handleVconConfirmUpload}>
              <div className="edit-position-grid">
                <div className="edit-section">
                  <div className="edit-section-title">Parsed Trade Details</div>
                  <div className="edit-section-fields">
                    {["B/S", "BB Ticker", "ISIN", "Trade Date", "Settle Date", "Price", "Notional Amount"].map((field: string) => (
                      <div className="edit-field-row" key={field}>
                        <label className="edit-field-label">{field}</label>
                        <span className="edit-position-input" style={{ background: "#f5f5f5", padding: "4px 8px" }}>{parsedVcon[field] !== undefined ? String(parsedVcon[field]) : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="edit-section">
                  <div className="edit-section-title">Additional / Editable</div>
                  <div className="edit-section-fields">
                    <div className="edit-field-row">
                      <label className="edit-field-label">Location</label>
                      <input type="text" className="edit-position-input" value={parsedVcon["Location"] || ""} onChange={(e) => setParsedVcon({ ...parsedVcon, "Location": e.target.value })} />
                    </div>
                    {["Counter Party", "Currency", "Seq No", "Settlement Amount", "Principal", "Accrued Interest", "Yield", "Cuisp"].map((field: string) => (
                      <div className="edit-field-row" key={field}>
                        <label className="edit-field-label">{field}</label>
                        <span className="edit-position-input" style={{ background: "#f5f5f5", padding: "4px 8px" }}>{parsedVcon[field] !== undefined ? String(parsedVcon[field]) : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="edit-position-footer">
                <button type="button" className="btn btn-cancel" onClick={() => { setVconStep(1); setParsedVcon(null); }}>Back</button>
                <button type="submit" className="btn upload-btn" disabled={request}>{request ? "Uploading..." : "Confirm and Upload"}</button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div style={{ display: tradeHistoryCardDisplay }} onDoubleClick={() => setTradeHistoryCardDisplay("none")}>
        {!positionInfo || (!positionInfo["ISIN"] && !positionInfo["Location"] && !positionInfo["BB Ticker"]) ? "" : <ViewTradeHistory setTradeHistoryCardDisplay={setTradeHistoryCardDisplay} positionInfo={positionInfo} date={""} />}
      </div>

    </div>
  );
}

export default HiddenPositions;
